import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { HcpAlert, ALERT_SIZE, SEVERITY } from '@hmhco/hcp-alert';
import TenantPickerLayout from '../../Layout/TenantPickerLayout';
import Logo from '../../Logo/Logo';
import useStyles from './EarlyAccessCreateUserPage.styles';
import RequiredFieldsNote from '../../RequiredFieldsNote/RequiredFieldsNote';
import NameField from './NameField';
import UsernameField from './UsernameField';
import SchoolAssociationField from './SchoolAssociationField';
import CreateUserButton from './CreateUserButton';
import nameFieldValidator from '../../../../util/nameFieldValidator';
import usernameValidator from '../../../../util/usernameValidator';
import { AUTO_COMPLETE } from '../../../../constants/constants';
import passwordValidator from '../../../../util/passwordValidator';
import formDataValidator from '../../../../util/formDataValidator';
import PasswordField from './PasswordField';
import {
  setEarlyAccessCreateUserFirstName,
  setEarlyAccessCreateUserLastName,
  setEarlyAccessCreateUserUsername,
  setEarlyAccessCreateUserPassword,
  setEarlyAccessCreateUserAssociatedSchools,
  setEarlyAccessCreateUserStep,
  setEarlyAccessCreateUserLoading,
  setEarlyAccessCreateUserSuccess,
} from '../../../../store/actions/EarlyAccessCreateUserActions';
import {
  getEarlyAccessFirstName,
  getEarlyAccessLastName,
  getEarlyAccessUsername,
  getEarlyAccessPassword,
  getEarlyAccessAssociatedSchools,
  getEarlyAccessCreateUserStep,
  getEarlyAccessCreateUserIsErrored,
  getEarlyAccessCreateUserHasSucceeded,
} from '../../../../store/selectors/EarlyAccessCreateUserState';

const errorMessageIds = {
  usernameExists: 'earlyAccess.createUser.username.exists',
  failedToCreateUser: 'earlyAccess.createUser.message.error.failed',
  schoolAssociationRequired:
    'earlyAccess.createUser.schoolAssociation.required',
};

const EarlyAccessCreateUserPage = () => {
  const { classes } = useStyles();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const firstName = useSelector(getEarlyAccessFirstName) ?? '';
  const lastName = useSelector(getEarlyAccessLastName) ?? '';
  const username = useSelector(getEarlyAccessUsername) ?? '';
  const password = useSelector(getEarlyAccessPassword);
  const schoolAssociation = useSelector(getEarlyAccessAssociatedSchools);
  const step = useSelector(getEarlyAccessCreateUserStep);
  const isErrored = useSelector(getEarlyAccessCreateUserIsErrored);
  const hasSucceeded = useSelector(getEarlyAccessCreateUserHasSucceeded);

  const [verifyPassword, setVerifyPassword] = useState(undefined);
  const [touched, setTouched] = useState({});

  const firstNameLabel = formatMessage({
    id: 'earlyAccess.createUser.label.firstName',
  });
  const lastNameLabel = formatMessage({
    id: 'earlyAccess.createUser.label.lastName',
  });

  const onBack = () => {
    dispatch(setEarlyAccessCreateUserStep(1));
  };

  const getErrors = currentDetails => {
    const errors = {};

    const {
      isNameValid: isFirstNameValid,
      nameErrorMessageId: firstNameErrorMessageId,
    } = nameFieldValidator(currentDetails.firstName, 'firstName');

    const {
      isNameValid: isLastNameValid,
      nameErrorMessageId: lastNameErrorMessageId,
    } = nameFieldValidator(currentDetails.lastName, 'lastName');

    const { isUsernameValid, usernameErrorMessageId } = usernameValidator(
      currentDetails.username,
    );

    if (!isFirstNameValid) {
      errors.firstName = firstNameErrorMessageId;
    }

    if (!isLastNameValid) {
      errors.lastName = lastNameErrorMessageId;
    }

    if (!isUsernameValid) {
      errors.username = usernameErrorMessageId;
    }

    if (!currentDetails.schoolAssociation) {
      errors.schoolAssociation = errorMessageIds.schoolAssociationRequired;
    }

    return errors;
  };

  const handleChange = event => {
    event.persist();
    const { id, value } = event.target;

    if (id === 'firstName') dispatch(setEarlyAccessCreateUserFirstName(value));
    if (id === 'lastName') dispatch(setEarlyAccessCreateUserLastName(value));
    if (id === 'username') dispatch(setEarlyAccessCreateUserUsername(value));

    setTouched(currentTouched => ({ ...currentTouched, [id]: true }));
  };

  const handleBlur = event => {
    event.persist();
    const { id } = event.target;
    setTouched(currentTouched => ({ ...currentTouched, [id]: true }));
  };

  const handleSchoolChange = selectedSchool => {
    dispatch(setEarlyAccessCreateUserAssociatedSchools(selectedSchool));
    setTouched(currentTouched => ({
      ...currentTouched,
      schoolAssociation: true,
    }));
  };

  const { isPasswordValid, passwordErrorMessageId } = passwordValidator(
    password ?? undefined,
  );

  const { passwordsDoNotMatchErrorMessageId } = formDataValidator(
    password,
    verifyPassword,
  );

  const isStep1Complete =
    firstName !== '' && lastName !== '' && Boolean(schoolAssociation);

  const handleNextStep = () => {
    dispatch(setEarlyAccessCreateUserStep(2));
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (step !== 2) return;
    dispatch(setEarlyAccessCreateUserLoading());
    // TODO: replace timeout with real API call
    setTimeout(() => dispatch(setEarlyAccessCreateUserSuccess()), 2000);
  };

  const errors = getErrors({
    firstName,
    lastName,
    username,
    schoolAssociation,
  });

  const verifyPasswordErrorMessage = passwordsDoNotMatchErrorMessageId
    ? formatMessage({
        id: passwordsDoNotMatchErrorMessageId,
      })
    : undefined;
  const passwordLabel = formatMessage({
    id: 'tenantPicker.newPassword.newPasswordLabel',
  });
  const reEnterPasswordLabel = formatMessage({
    id: 'tenantPicker.newPassword.reEnterNewPasswordLabel',
  });
  const passwordErrorMessage = passwordErrorMessageId
    ? formatMessage({
        id: passwordErrorMessageId,
      })
    : undefined;

  const handlePasswordChange = event => {
    dispatch(setEarlyAccessCreateUserPassword(event.target.value));
  };
  const handleVerifyPasswordChange = event =>
    setVerifyPassword(event.target.value);

  return (
    <TenantPickerLayout>
      <header>
        <Logo />
        {!hasSucceeded && (
          <div className={classes.title}>
            <Typography component="h1" variant="h3">
              <FormattedMessage id="earlyAccess.createUser.title" />
            </Typography>
            <RequiredFieldsNote />
          </div>
        )}
      </header>
      <main>
        {hasSucceeded ? (
          <>
            <HcpAlert
              size={ALERT_SIZE.SMALL}
              severity={SEVERITY.SUCCESS}
              title={formatMessage({
                id: 'earlyAccess.createUser.message.success.title',
              })}
              subtitle={formatMessage({
                id: 'earlyAccess.createUser.message.success.body',
              })}
            />
            <div className={classes.successButton}>
              <Button variant="outlined" color="primary">
                <FormattedMessage id="earlyAccess.createUser.button.signIn" />
              </Button>
            </div>
          </>
        ) : (
          <>
            {isErrored && (
              <HcpAlert
                size={ALERT_SIZE.SMALL}
                severity={SEVERITY.ERROR}
                title={formatMessage({
                  id: 'earlyAccess.createUser.message.error.failed',
                })}
              />
            )}
            <form className={classes.form} onSubmit={handleSubmit}>
              {step === 1 && (
                <>
                  <SchoolAssociationField
                    selectedSchools={schoolAssociation}
                    errorMessageId={
                      touched.schoolAssociation && errors.schoolAssociation
                    }
                    handleChange={handleSchoolChange}
                  />
                  <NameField
                    name={firstName}
                    nameLabel={firstNameLabel}
                    errorMessageId={touched.firstName && errors.firstName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    elementId="firstName"
                  />
                  <NameField
                    name={lastName}
                    nameLabel={lastNameLabel}
                    errorMessageId={touched.lastName && errors.lastName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    elementId="lastName"
                    autoComplete={AUTO_COMPLETE.FAMILY_NAME}
                  />
                  <CreateUserButton
                    isDisabled={!isStep1Complete}
                    onClick={handleNextStep}
                    type="button"
                    labelId="earlyAccess.createUser.button.next"
                  />
                </>
              )}
              {step === 2 && (
                <>
                  <UsernameField
                    username={username}
                    errorMessageId={touched.username && errors.username}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                  <Typography variant="body2">
                    <FormattedMessage id="earlyAccess.usernameRequirements" />
                  </Typography>
                  <PasswordField
                    dataTestId="new-password-password"
                    handlePasswordChange={handlePasswordChange}
                    errorMessage={passwordErrorMessage}
                    passwordLabel={passwordLabel}
                    elementId="new-password-enter-password"
                  />
                  <PasswordField
                    dataTestId="new-password-validate-password"
                    handlePasswordChange={handleVerifyPasswordChange}
                    errorMessage={verifyPasswordErrorMessage}
                    passwordLabel={reEnterPasswordLabel}
                    elementId="new-password-reenter-password"
                  />
                  <CreateUserButton
                    onBack={onBack}
                    isDisabled={!isPasswordValid}
                  />
                </>
              )}
            </form>
          </>
        )}
      </main>
    </TenantPickerLayout>
  );
};

export default EarlyAccessCreateUserPage;
